import {ReactNode, useEffect, useMemo, useState} from "react";
import {css, cx} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";

import {useShouldShowPriceByOfferConfigAndUser} from "../hooks/use_should_show_price_by_offer_config_and_user";
import {IMapOfferSidebar} from "../offer/map/types/MapOfferSidebar";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {getOfferDisplayStatus} from "../offer/utils/get_offer_display_status";
import {PropertyPlanModal} from "../property/components/property_plan_modal/PropertyPlanModal";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {ShowPropertyPlanView, showPropertyPlanView} from "../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {IAmplitudeItemListContext} from "../tracking/amplitude/get_amplitude_view_item_event_properties";
import {useGTMEcommercePropertyListViewTracking} from "../tracking/hooks/use_gtm_ecommerce_property_list_view_tracking";
import {PropertyListLoader} from "./PropertyListLoader";
import {PropertyListTableHeader} from "./PropertyListTableHeader";
import {PropertyListTableRow} from "./PropertyListTableRow";

export interface IProps {
    offer: IOfferDetail | IMapOfferSidebar;
    properties: IPropertyListProperty[];
    requestState: RequestState;
    sort: string | null;
    onSortChange: (value: string) => void;
    className?: string;
    onPageChange: (value: number) => Promise<unknown[] | void>;
    pageCount: number;
    pageIsLoading: boolean;
    currentPage: number;
    withoutLink?: boolean;
    hideCell?: {
        propertyPlan?: boolean;
        propertyNumber?: boolean;
        propertyRooms?: boolean;
        propertyArea?: boolean;
        plotArea?: boolean;
        propertyFloor?: boolean;
        propertyPrice?: boolean;
        propertyGotoLink?: boolean;
    };
    handleCellClick?: (value: number) => void;
    textLinkButton?: boolean;
    icon?: ReactNode;
    loaderWrapperBackground?: string;
    caption?: string;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    trackingMeta: {
        listId: string;
        listName: string;
    };
}

export const propertiesSortOptions = {
    NUMBER: {value: "number", shortName: "Numer", showOnMobile: false},
    PRICE: {value: "price", shortName: "Cena", showOnMobile: true},
    ROOMS: {value: "rooms", shortName: "Pokoje", showOnMobile: true},
    FLOOR: {value: "floor", shortName: "Piętro", showOnMobile: false},
    PLOT_AREA: {value: "plot_area", shortName: "Pow. działki", showOnMobile: false},
    AREA: {value: "area", shortName: "Metraż", showOnMobile: true}
};

export const PropertyListTable = (props: IProps) => {
    const {
        offer,
        properties,
        requestState,
        sort,
        onSortChange,
        className,
        onPageChange,
        pageCount,
        pageIsLoading,
        currentPage,
        withoutLink,
        hideCell,
        handleCellClick,
        textLinkButton,
        icon,
        loaderWrapperBackground,
        caption,
        amplitudeItemListContext,
        trackingMeta
    } = props;

    const [isModalOpened, setIsModalOpened] = useState(false);
    const [currentProperty, setCurrentProperty] = useState(0);

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        vendorId: offer.vendor.id
    });

    const processedProperties = useMemo(() => {
        if (!shouldShowPrice) {
            return properties.map((property) => ({
                ...property,
                price: null
            }));
        }

        return properties;
    }, [properties, shouldShowPrice]);

    const {containerId} = useGTMEcommercePropertyListViewTracking({
        offer,
        properties: processedProperties,
        listId: trackingMeta.listId,
        listName: trackingMeta.listName,
        useVisibilityTracking: true
    });
    useEffect(() => {
        if (!isModalOpened && currentProperty !== 0) {
            setCurrentProperty(0);
        }
    }, [isModalOpened]);

    const openModal = () => setIsModalOpened(true);

    const closeModal = () => setIsModalOpened(false);

    const handleSortChange = (value: string) => {
        onSortChange(value);
    };

    const onPlanClick = (e: React.MouseEvent<HTMLElement>, index: number) => {
        e.preventDefault();
        setCurrentProperty(index);
        showPropertyPlanView(ShowPropertyPlanView.PROPERTY_LIST, offer, offer.vendor);
        openModal();
    };

    const onNextClick = async (currentIndex: number) => {
        if (currentIndex === processedProperties.length - 1) {
            setCurrentProperty(0);
            await onPageChange(currentPage + 1);
        } else {
            setCurrentProperty(currentIndex + 1);
        }
    };

    const onPrevClick = async (currentIndex: number) => {
        if (currentIndex === 0) {
            const results = await onPageChange(currentPage - 1);
            results && setCurrentProperty(results.length - 1);
        } else {
            setCurrentProperty(currentIndex - 1);
        }
    };

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;
    const showPriceColumn = !isForSaleOverLimit || (isForSaleOverLimit && isAutoresponderEnabled);

    const trackedData = {
        offer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        vendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        }
    };

    const onPlanButtonClick = () => {
        showPropertyPlanView(ShowPropertyPlanView.PLAN_IMAGE_DOWNLOAD, trackedData.offer, trackedData.vendor);
    };

    if (processedProperties.length > 0) {
        return (
            <div className={tableTrack} id={containerId} data-id={containerId}>
                <table className={cx(table, className)}>
                    {caption && <caption className={tableCaption}>{caption}</caption>}
                    <thead>
                        <PropertyListTableHeader
                            offer={offer}
                            sort={sort}
                            onSortChange={handleSortChange}
                            showPriceColumn={showPriceColumn}
                            hideCell={hideCell}
                        />
                    </thead>

                    <tbody>
                        {processedProperties.map((property, index) => {
                            return (
                                <PropertyListTableRow
                                    key={property.id}
                                    offer={offer}
                                    property={property}
                                    sort={sort}
                                    onPlanClick={onPlanClick}
                                    index={index}
                                    showPriceColumn={showPriceColumn}
                                    withoutLink={withoutLink}
                                    hideCell={hideCell}
                                    handleCellClick={handleCellClick}
                                    textLinkButton={textLinkButton}
                                    icon={icon}
                                    amplitudeItemListContext={amplitudeItemListContext}
                                    trackingMeta={trackingMeta}
                                />
                            );
                        })}
                    </tbody>
                </table>

                <PropertyPlanModal
                    isOpen={isModalOpened}
                    property={processedProperties[currentProperty]}
                    offer={offer}
                    pageCount={pageCount}
                    pageIsLoading={pageIsLoading}
                    currentPropertyIndex={currentProperty}
                    currentPage={currentPage}
                    propertiesOnCurrentPage={processedProperties.length}
                    handleClose={closeModal}
                    handleNextClick={onNextClick}
                    handlePrevClick={onPrevClick}
                    onPlanDownloadClick={onPlanButtonClick}
                />

                {requestState === RequestState.Waiting && <PropertyListLoader backgroundColor={loaderWrapperBackground} useTopOffset />}
            </div>
        );
    } else if (requestState === RequestState.Waiting) {
        return (
            <div className={loaderWrapNoProperties}>
                <Loader />
            </div>
        );
    } else {
        return <div className={noOffersText}>Brak ofert do wyświetlenia</div>;
    }
};

// tableTrack added for tiny 320px screens like iPhone 5/SE (2016)
// does not allow table to stretch the layout off-screen,  but still enables horizontal scroll
const tableTrack = css({
    position: "relative",
    maxWidth: "100vw",
    overflowX: "auto"
});

const table = css({
    backgroundColor: "var(--colors-white)",
    width: "100%",
    marginBottom: "1.6rem",
    position: "relative",
    borderCollapse: "collapse",

    md: {
        marginBottom: "2.9rem",
        backgroundColor: "transparent"
    }
});

const tableCaption = css({
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: "0",
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: "0"
});

const loaderWrapNoProperties = css({
    maxHeight: "100%",
    maxWidth: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const noOffersText = css({
    fontSize: "1.6rem",
    lineHeight: "2.4rem",
    padding: "1.4rem"
});
import {ReactNode, useEffect, useMemo, useState} from "react";
import {css, cx} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";

import {useShouldShowPriceByOfferConfigAndUser} from "../hooks/use_should_show_price_by_offer_config_and_user";
import {IMapOfferSidebar} from "../offer/map/types/MapOfferSidebar";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {getOfferDisplayStatus} from "../offer/utils/get_offer_display_status";
import {PropertyPlanModal} from "../property/components/property_plan_modal/PropertyPlanModal";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {ShowPropertyPlanView, showPropertyPlanView} from "../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {IAmplitudeItemListContext} from "../tracking/amplitude/get_amplitude_view_item_event_properties";
import {useGTMEcommercePropertyListViewTracking} from "../tracking/hooks/use_gtm_ecommerce_property_list_view_tracking";
import {PropertyListLoader} from "./PropertyListLoader";
import {PropertyListTableHeader} from "./PropertyListTableHeader";
import {PropertyListTableRow} from "./PropertyListTableRow";

export interface IProps {
    offer: IOfferDetail | IMapOfferSidebar;
    properties: IPropertyListProperty[];
    requestState: RequestState;
    sort: string | null;
    onSortChange: (value: string) => void;
    className?: string;
    onPageChange: (value: number) => Promise<unknown[] | void>;
    pageCount: number;
    pageIsLoading: boolean;
    currentPage: number;
    withoutLink?: boolean;
    hideCell?: {
        propertyPlan?: boolean;
        propertyNumber?: boolean;
        propertyRooms?: boolean;
        propertyArea?: boolean;
        plotArea?: boolean;
        propertyFloor?: boolean;
        propertyPrice?: boolean;
        propertyGotoLink?: boolean;
    };
    handleCellClick?: (value: number) => void;
    textLinkButton?: boolean;
    icon?: ReactNode;
    loaderWrapperBackground?: string;
    caption?: string;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    trackingMeta: {
        listId: string;
        listName: string;
    };
}

export const propertiesSortOptions = {
    NUMBER: {value: "number", shortName: "Numer", showOnMobile: false},
    PRICE: {value: "price", shortName: "Cena", showOnMobile: true},
    ROOMS: {value: "rooms", shortName: "Pokoje", showOnMobile: true},
    FLOOR: {value: "floor", shortName: "Piętro", showOnMobile: false},
    PLOT_AREA: {value: "plot_area", shortName: "Pow. działki", showOnMobile: false},
    AREA: {value: "area", shortName: "Metraż", showOnMobile: true}
};

export const PropertyListTable = (props: IProps) => {
    const {
        offer,
        properties,
        requestState,
        sort,
        onSortChange,
        className,
        onPageChange,
        pageCount,
        pageIsLoading,
        currentPage,
        withoutLink,
        hideCell,
        handleCellClick,
        textLinkButton,
        icon,
        loaderWrapperBackground,
        caption,
        amplitudeItemListContext,
        trackingMeta
    } = props;

    const [isModalOpened, setIsModalOpened] = useState(false);
    const [currentProperty, setCurrentProperty] = useState(0);

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        vendorId: offer.vendor.id
    });

    const processedProperties = useMemo(() => {
        if (!shouldShowPrice) {
            return properties.map((property) => ({
                ...property,
                price: null
            }));
        }

        return properties;
    }, [properties, shouldShowPrice]);

    const {containerId} = useGTMEcommercePropertyListViewTracking({
        offer,
        properties: processedProperties,
        listId: trackingMeta.listId,
        listName: trackingMeta.listName,
        useVisibilityTracking: true
    });
    useEffect(() => {
        if (!isModalOpened && currentProperty !== 0) {
            setCurrentProperty(0);
        }
    }, [isModalOpened]);

    const openModal = () => setIsModalOpened(true);

    const closeModal = () => setIsModalOpened(false);

    const handleSortChange = (value: string) => {
        onSortChange(value);
    };

    const onPlanClick = (e: React.MouseEvent<HTMLElement>, index: number) => {
        e.preventDefault();
        setCurrentProperty(index);
        showPropertyPlanView(ShowPropertyPlanView.PROPERTY_LIST, offer, offer.vendor);
        openModal();
    };

    const onNextClick = async (currentIndex: number) => {
        if (currentIndex === processedProperties.length - 1) {
            setCurrentProperty(0);
            await onPageChange(currentPage + 1);
        } else {
            setCurrentProperty(currentIndex + 1);
        }
    };

    const onPrevClick = async (currentIndex: number) => {
        if (currentIndex === 0) {
            const results = await onPageChange(currentPage - 1);
            results && setCurrentProperty(results.length - 1);
        } else {
            setCurrentProperty(currentIndex - 1);
        }
    };

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;
    const showPriceColumn = !isForSaleOverLimit || (isForSaleOverLimit && isAutoresponderEnabled);

    const trackedData = {
        offer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        vendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        }
    };

    const onPlanButtonClick = () => {
        showPropertyPlanView(ShowPropertyPlanView.PLAN_IMAGE_DOWNLOAD, trackedData.offer, trackedData.vendor);
    };

    if (processedProperties.length > 0) {
        return (
            <div className={tableTrack} id={containerId} data-id={containerId}>
                <table className={cx(table, className)}>
                    {caption && <caption className={tableCaption}>{caption}</caption>}
                    <thead>
                        <PropertyListTableHeader
                            offer={offer}
                            sort={sort}
                            onSortChange={handleSortChange}
                            showPriceColumn={showPriceColumn}
                            hideCell={hideCell}
                        />
                    </thead>

                    <tbody>
                        {processedProperties.map((property, index) => {
                            return (
                                <PropertyListTableRow
                                    key={property.id}
                                    offer={offer}
                                    property={property}
                                    sort={sort}
                                    onPlanClick={onPlanClick}
                                    index={index}
                                    showPriceColumn={showPriceColumn}
                                    withoutLink={withoutLink}
                                    hideCell={hideCell}
                                    handleCellClick={handleCellClick}
                                    textLinkButton={textLinkButton}
                                    icon={icon}
                                    amplitudeItemListContext={amplitudeItemListContext}
                                    trackingMeta={trackingMeta}
                                />
                            );
                        })}
                    </tbody>
                </table>

                <PropertyPlanModal
                    isOpen={isModalOpened}
                    property={processedProperties[currentProperty]}
                    offer={offer}
                    pageCount={pageCount}
                    pageIsLoading={pageIsLoading}
                    currentPropertyIndex={currentProperty}
                    currentPage={currentPage}
                    propertiesOnCurrentPage={processedProperties.length}
                    handleClose={closeModal}
                    handleNextClick={onNextClick}
                    handlePrevClick={onPrevClick}
                    onPlanDownloadClick={onPlanButtonClick}
                />

                {requestState === RequestState.Waiting && <PropertyListLoader backgroundColor={loaderWrapperBackground} useTopOffset />}
            </div>
        );
    } else if (requestState === RequestState.Waiting) {
        return (
            <div className={loaderWrapNoProperties}>
                <Loader />
            </div>
        );
    } else {
        return <div className={noOffersText}>Brak ofert do wyświetlenia</div>;
    }
};

// tableTrack added for tiny 320px screens like iPhone 5/SE (2016)
// does not allow table to stretch the layout off-screen,  but still enables horizontal scroll
const tableTrack = css({
    position: "relative",
    maxWidth: "100vw",
    overflowX: "auto"
});

const table = css({
    backgroundColor: "var(--colors-white)",
    width: "100%",
    marginBottom: "1.6rem",
    position: "relative",
    borderCollapse: "collapse",

    md: {
        marginBottom: "2.9rem",
        backgroundColor: "transparent"
    }
});

const tableCaption = css({
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: "0",
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: "0"
});

const loaderWrapNoProperties = css({
    maxHeight: "100%",
    maxWidth: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const noOffersText = css({
    fontSize: "1.6rem",
    lineHeight: "2.4rem",
    padding: "1.4rem"
});
